import {
  Box,
  Center,
  Stack,
  Text,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import theme from "@chakra-ui/theme";
import Tweets from "components/tweets";
import { useContext, useEffect, useState, useRef } from "react";
import { getToken } from "../firebaseClient";
import { AILanguageOptions } from "../utils/AILanguageOptions";
import { useSession } from "next-auth/react";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import ProtectedRoute from "components/protected-route";

import { Page } from "components/page";
import { useRouter } from "next/dist/client/router";
import PuffLoader from "react-spinners/PuffLoader";
import * as analytics from "../utils/analytics";
import { getAccount } from "../utils/sessionHelper";
import { textStyle, variant } from "theme/names";
import { getCachedKey, getCachedKeyTime, updateCachedKey, updateCachedKeyTime } from "../utils/localforageHelpers";

export const Suggested = () => {

  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"

  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<any>(null);
  const router = useRouter();

  const [refresh, refresher] = useState({});
  const [hasFetched, setHasFetched] = useState(false);

  const [cachedPostsLoaded, setCachedPostsLoaded] = useState(false);
  const miscellaneousContext: any = useMiscellaneousContext();
  const clearCache = useRef(false);

  useEffect(() => {
    analytics.init(session);
    if (session?.user?.data?.id && (!hasFetched || clearCache?.current === true)) {
      getTweets(session);
    }
  }, [session, clearCache]);

  const getLanguageForNews = () => {
    const langFromPreferred = AILanguageOptions.find(lang => lang.value.includes(getAccount(session)?.languagePreferred)) || { value: 'English' };
    langFromPreferred.value = (langFromPreferred?.value?.toLowerCase().includes('english')) ? 'English' : langFromPreferred?.value; //for 'American English / Britain English' values from backend.
    const newsLang = languagesAvailable[langFromPreferred?.value] ?? "en";
    return newsLang;
  }

  // const sendRequest = React.useCallback(async (session, topic) => {
  const getTweets = async (session) => {
    if (isSending && !clearCache.current) return;
    setIsSending(true);
    miscellaneousContext.setIsDataRefreshing(true);
    let cachedPosts: any = await getCachedKey("cachedNewsPosts");
    let cachedPostsTime = await getCachedKeyTime("cachedNewsPostsTime");
    const aDayFromNow = new Date().getTime() + 24 * 60 * 60 * 1000;
    const lang = getLanguageForNews();

    if (Boolean(cachedPostsTime) === false) {
      await updateCachedKeyTime("cachedNewsPostsTime", aDayFromNow);
    }

    if (
      new Date().getTime() <= new Date(cachedPostsTime).getTime() &&
      !clearCache.current
    ) {
      if (Boolean(cachedPosts) === true && cachedPosts?.tweets?.length > 0) {
        console.log("cache hit");
        setCachedPostsLoaded(true);
        setData(cachedPosts);
        setHasFetched(true);
        setIsSending(false);
        miscellaneousContext.setIsDataRefreshing(false);
        return;
      } else {
        console.log("cache miss");
        setCachedPostsLoaded(false);
      }
    }
    if (!cachedPostsLoaded || clearCache.current) {
      console.log("caching...");
      let url = `https://api.tweetbutler.com/news?isInfluentLeader=true&id=${getAccount(session).idAccount}&count=10&keywords=${getAccount(session).keywords.join(",")}&description=${encodeURIComponent(getAccount(session).description)}&language=${lang}`;
      // console.log(lang, 'language check')
      const token = await getToken(session, "news");
      let response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          tokenuserid: session.user.uid,
          Authorization: `Bearer ${token}`,
        },
      });
      let dataNews = await response.json();
      console.log(dataNews);
      let news = [];
      if (dataNews.success && dataNews.news) {
        news = dataNews.news.map(n => {
          let body = n.title + "\n\n" + n.description;
          if (body.length > 220)
            body = body.substring(0, 220) + "...";
          return {
            ...n,
            // type: "news",
            full_text: body + "\n\n" + n.url,
            news: n,
            extended_entities: {
              media: [
                { media_url_https: n.urlToImage },
              ]
            },
            user: {
              //@ts-ignore
              profile_image_url_https: getAccount(session).image,
              name: getAccount(session).name,
              screen_name: getAccount(session).username,
            }
          };
        });
      }
      setData({ tweets: news });
      await updateCachedKey("cachedNewsPosts", { tweets: news });
      clearCache.current = false;
      await updateCachedKeyTime("cachedNewsPostsTime", aDayFromNow);
      setHasFetched(true);
      setIsSending(false);
      miscellaneousContext.setIsDataRefreshing(false);
    }
  };

  const refreshClickedCallback = () => {
    setIsSending(false);
    clearCache.current = true;
  };

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  return (
    <Page
      title="Top News"
      description="Here are news from your industry. Post them directly or add your unique point of view."
      infoTooltipText="These posts were selected for you by our AI engine, based on your parameters set in the settings section."
      isLoadingFeature={isSending}
      showLoadingBar={true}
    >
      <Box mb={5}>
        <Button
          variant={variant.Button.tertiaryNoBackground}
          onClick={() => {
            router.push("/settings#search-settings");
          }}
        >
          Edit my personalized feed
        </Button>
      </Box>
      <Stack mt={0}>
        {
          isSending && !clearCache.current ? (
            <Center pt={10}>
              <PuffLoader color={"#0A66C2"} loading={isSending} cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }} size={50} />
            </Center>
          ) : (
            //@ts-ignore
            (!isSending || clearCache.current) && data?.tweets?.length ? (
              <Box>
                <Tweets
                  //@ts-ignore
                  sendRequest={getTweets}
                  //@ts-ignore
                  tweets={data.tweets}
                  //@ts-ignore
                  data={data}
                  //@ts-ignore
                  setData={setData}
                  disableBlur={true}
                  disableBookmarkButton={true}
                  refresher={refresher}
                  refresh={refresh}
                  disableSaveCsv={false}
                  isExportButtonSecondary={true}
                  disableEmbed={true}
                  disableTryAgain={true}
                  disableHideButton={true}
                  disableRequest={true}
                  disableSubMenu={true}
                  disableName={false}
                  disableRefresh={false}
                  isVariation={true}
                  source="suggested"
                  disableAggreeDisagree={false}
                  refreshClickedCallback={refreshClickedCallback}
                />
              </Box>
            ) : (
              <Box>
                <Center m={10} flexDirection="column">
                  <Text textStyle={textStyle["body.medium.light"]} m={5}>Nothing yet</Text>
                  <Text textStyle={textStyle["body.medium.light"]}>Our team is currenty writing content for you</Text>
                </Center>
              </Box>
            )
          )
        }
      </Stack>
    </Page>
  );
};

const languagesAvailable = {
  'Arabic': 'ar',
  'German': 'de',
  'English': 'en',
  'Spanish': 'es',
  'French': 'fr',
  'Hebrew': 'he',
  'Italian': 'it',
  'Dutch': 'nl',
  'Norwegian': 'no',
  'Portuguese': 'pt',
  'Russian': 'ru',
  'Swedish': 'sv',
  'Urdu': 'ud',
  'Chinese': 'zh'
};

export default Suggested;
